// MemberReferrals.tsx - Section parrainages avec responsive design
import React, { useState } from 'react';
import ShareSection from '../adhesion/ShareSection';
import { Users, Share2, Copy, MessageCircle, Plus, Target, Award } from 'lucide-react';

interface MemberReferralsProps {
    memberData: any;
    shareLinks: any;
    copySuccess: boolean;
    onCopyLink: () => void;
    onShareWhatsApp: () => void;
    onShareSocial: (platform: 'telegram' | 'twitter' | 'facebook' | 'linkedin') => void;
}

const MemberReferrals: React.FC<MemberReferralsProps> = ({
    memberData,
    shareLinks,
    copySuccess,
    onCopyLink,
    onShareWhatsApp,
    onShareSocial
}) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [showShareModal, setShowShareModal] = useState(false);
    const referralsCount = Number(memberData.referrals_count) || 0;

    // Calculer les statistiques
    const getNextBadgeTarget = () => {
        if (referralsCount < 5) return 5;
        if (referralsCount < 25) return 25;
        if (referralsCount < 50) return 50;
        if (referralsCount < 100) return 100;
        return null;
    };

    const nextTarget = getNextBadgeTarget();
    const progressPercentage = nextTarget ? (referralsCount / nextTarget) * 100 : 100;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-green-100 rounded-lg">
                        <Users className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Mes parrainages</h1>
                        <p className="text-gray-600">Invitez de nouveaux membres et gagnez des badges</p>
                    </div>
                </div>

                {/* Statistiques principales */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border border-green-200">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <Users className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-green-600">Total parrainages</p>
                                <p className="text-2xl font-bold text-green-800">{referralsCount}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <Target className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-blue-600">Prochain objectif</p>
                                <p className="text-2xl font-bold text-blue-800">
                                    {nextTarget ? `${nextTarget - referralsCount} restant${nextTarget - referralsCount > 1 ? 's' : ''}` : 'Max atteint'}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 bg-orange-100 rounded-lg">
                                <Award className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-orange-600">Progression</p>
                                <p className="text-2xl font-bold text-orange-800">{Math.round(progressPercentage)}%</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Progression vers le prochain badge */}
            {nextTarget && (
                <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900">Progression vers le prochain badge</h2>
                        <div className="text-sm text-gray-500">
                            {referralsCount}/{nextTarget}
                        </div>
                    </div>
                    
                    <div className="mb-4">
                        <div className="bg-gray-200 rounded-full h-3">
                            <div 
                                className="bg-gradient-to-r from-green-500 to-blue-500 h-3 rounded-full transition-all duration-500"
                                style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                            ></div>
                        </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="text-blue-800 font-medium">
                            Plus que <strong>{nextTarget - referralsCount}</strong> parrainage{nextTarget - referralsCount > 1 ? 's' : ''} 
                            pour débloquer votre prochain badge ! 🎯
                        </p>
                    </div>
                </div>
            )}

            {/* Message d'encouragement si pas de parrainage */}
            {referralsCount === 0 && (
                <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-6 border border-blue-200">
                    <div className="flex items-start space-x-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <Plus className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-blue-900">
                                Commencez à parrainer ! 🚀
                            </h3>
                            <p className="text-blue-700 mt-1 mb-4">
                                Invitez vos proches, amis et collègues à rejoindre l'Union Démocratique des Bâtisseurs. 
                                Votre premier parrainage vous rapprochera du badge "Recruteur" !
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <button
                                    onClick={onShareWhatsApp}
                                    className="flex items-center justify-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                                >
                                    <MessageCircle className="w-4 h-4" />
                                    <span>Partager sur WhatsApp</span>
                                </button>
                                <button
                                    onClick={() => setShowShareModal(true)}
                                    className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    <Share2 className="w-4 h-4" />
                                    <span>Autres plateformes</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Section de partage */}
            {shareLinks && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-6">Partagez votre lien de parrainage</h2>
                        <ShareSection
                            referralLink={shareLinks.referralLink}
                            copySuccess={copySuccess}
                            onCopyLink={onCopyLink}
                            onShareWhatsApp={onShareWhatsApp}
                            onShareSocial={onShareSocial}
                            referralCode={memberData.referral_code}
                        />
                    </div>
                </div>
            )}

            {/* Conseils pour parrainer */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-yellow-800 mb-4">
                    💡 Conseils pour réussir vos parrainages
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h4 className="font-semibold text-yellow-900 mb-2">Approche personnelle</h4>
                        <ul className="space-y-2 text-sm text-yellow-800">
                            <li className="flex items-start space-x-2">
                                <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-2 flex-shrink-0"></div>
                                <span>Expliquez pourquoi vous avez rejoint l'UDB</span>
                            </li>
                            <li className="flex items-start space-x-2">
                                <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-2 flex-shrink-0"></div>
                                <span>Partagez votre vision du changement politique</span>
                            </li>
                            <li className="flex items-start space-x-2">
                                <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-2 flex-shrink-0"></div>
                                <span>Mettez en avant les valeurs communes</span>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-semibold text-yellow-900 mb-2">Stratégies efficaces</h4>
                        <ul className="space-y-2 text-sm text-yellow-800">
                            <li className="flex items-start space-x-2">
                                <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-2 flex-shrink-0"></div>
                                <span>Utilisez les groupes WhatsApp et réseaux sociaux</span>
                            </li>
                            <li className="flex items-start space-x-2">
                                <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-2 flex-shrink-0"></div>
                                <span>Profitez des événements et rassemblements</span>
                            </li>
                            <li className="flex items-start space-x-2">
                                <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-2 flex-shrink-0"></div>
                                <span>Partagez régulièrement mais sans spam</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Informations sur le code de parrainage */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Votre code de parrainage</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Code personnel</p>
                            <p className="text-2xl font-bold text-gray-900 font-mono">
                                {memberData.referral_code}
                            </p>
                        </div>
                        <button
                            onClick={onCopyLink}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                                copySuccess 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            }`}
                        >
                            <Copy className="w-4 h-4" />
                            <span>{copySuccess ? 'Copié !' : 'Copier le lien'}</span>
                        </button>
                    </div>
                </div>
                
                <div className="mt-4 text-sm text-gray-600">
                    <p><strong>Comment ça marche :</strong></p>
                    <ol className="list-decimal list-inside space-y-1 mt-2">
                        <li>Partagez votre lien de parrainage personnel</li>
                        <li>Les nouveaux membres s'inscrivent via votre lien</li>
                        <li>Votre compteur de parrainages augmente automatiquement</li>
                        <li>Vous progressez vers de nouveaux badges et récompenses</li>
                    </ol>
                </div>
            </div>

            {/* Récompenses à venir */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-900 mb-4">
                    🎁 Récompenses à débloquer
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className={`p-3 rounded-lg border-2 ${referralsCount >= 5 ? 'bg-green-100 border-green-300' : 'bg-white border-dashed border-purple-300'}`}>
                        <div className="text-center">
                            <div className="text-2xl mb-1">🥉</div>
                            <div className="font-semibold text-sm">Recruteur</div>
                            <div className="text-xs text-gray-600">5 parrainages</div>
                        </div>
                    </div>
                    <div className={`p-3 rounded-lg border-2 ${referralsCount >= 25 ? 'bg-green-100 border-green-300' : 'bg-white border-dashed border-purple-300'}`}>
                        <div className="text-center">
                            <div className="text-2xl mb-1">🥈</div>
                            <div className="font-semibold text-sm">Bâtisseur Or</div>
                            <div className="text-xs text-gray-600">25 parrainages</div>
                        </div>
                    </div>
                    <div className={`p-3 rounded-lg border-2 ${referralsCount >= 50 ? 'bg-green-100 border-green-300' : 'bg-white border-dashed border-purple-300'}`}>
                        <div className="text-center">
                            <div className="text-2xl mb-1">🥇</div>
                            <div className="font-semibold text-sm">Ambassadeur</div>
                            <div className="text-xs text-gray-600">50 parrainages</div>
                        </div>
                    </div>
                    <div className={`p-3 rounded-lg border-2 ${referralsCount >= 100 ? 'bg-green-100 border-green-300' : 'bg-white border-dashed border-purple-300'}`}>
                        <div className="text-center">
                            <div className="text-2xl mb-1">👑</div>
                            <div className="font-semibold text-sm">Légende</div>
                            <div className="text-xs text-gray-600">100 parrainages</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MemberReferrals;